import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// Recovery for the "stale chunk after deploy" failure mode.
//
// Every route is code-split via `lazy(() => import('@/pages/…'))` and Vite emits
// content-hashed chunk filenames (e.g. `Body-Bz3J5Aub.js`). A new deploy rotates
// those hashes and removes the old files from wwwroot/assets. A returning user
// who still holds the *previous* index.html / running bundle (browser HTTP cache
// or the precached PWA shell — registerType: 'autoUpdate') requests the old
// chunk name on navigation, gets a 404, and `import()` rejects with
// "Failed to fetch dynamically imported module". That rejection bubbles through
// <Suspense> to the top-level ErrorBoundary and crashes the screen.
//
// The fix: when a dynamic import fails because the chunk is gone, force a single
// full reload so the browser/service-worker pulls the fresh index.html + chunk
// manifest. A sessionStorage-backed time window guards against a reload loop if
// the reload doesn't actually fix it (genuine outage / bad deploy) — in that
// case we fall through to the ErrorBoundary as before.

const RELOAD_GUARD_KEY = 'optimize.chunkReload.v1';
// Don't reload more than once per window. Long enough to cover the reload +
// reboot, short enough that a *later* deploy can still trigger a fresh recovery.
const RELOAD_WINDOW_MS = 10_000;

// Set once we've kicked off window.location.reload() in this page session so
// concurrent chunk failures (a route's lazy import and the global
// vite:preloadError listener fire for the same module) all just suspend and wait
// for the imminent navigation instead of racing each other to the ErrorBoundary.
let reloadInFlight = false;

/** Whether a recovery reload has already been started in this page session. */
export function isReloadInFlight(): boolean {
  return reloadInFlight;
}

/**
 * Recognises the various browser/Vite wordings for "a dynamically imported
 * module (or its preloaded CSS) failed to load". Anything else is a genuine
 * error we must not mask with a reload.
 */
export function isModuleLoadError(err: unknown): boolean {
  const message =
    err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  if (!message) return false;
  return (
    /failed to fetch dynamically imported module/i.test(message) || // Chrome/Edge
    /error loading dynamically imported module/i.test(message) || // Firefox
    /importing a module script failed/i.test(message) || // Safari
    /unable to preload css/i.test(message) // Vite preloadError (CSS dep)
  );
}

/**
 * Attempts a single full-page reload to recover from a stale chunk. Returns
 * `true` when a reload is underway (the caller should suspend / swallow the
 * error), `false` when we declined to reload (the caller should surface the
 * error to the ErrorBoundary).
 */
export function triggerStaleChunkReload(): boolean {
  if (reloadInFlight) return true;

  let recentlyReloaded = false;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
    recentlyReloaded = last > 0 && Date.now() - last < RELOAD_WINDOW_MS;
    if (!recentlyReloaded) {
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    }
  } catch {
    // No usable sessionStorage (private mode / quota): we can't guard against a
    // reload loop, so decline to reload rather than risk one. Degrades to the
    // pre-existing ErrorBoundary behaviour.
    return false;
  }

  // We reloaded moments ago and the chunk *still* failed — another reload won't
  // help. Surface the error instead of looping.
  if (recentlyReloaded) return false;

  reloadInFlight = true;
  window.location.reload();
  return true;
}

/**
 * Drop-in replacement for React's `lazy()` that auto-recovers from a stale chunk
 * by reloading once. On any other failure (or if a reload won't help) it rethrows
 * so the ErrorBoundary still handles genuine bugs.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err: unknown) => {
      // If a reload is already underway (e.g. the global vite:preloadError handler
      // fired first and called preventDefault, which can turn the original 404
      // into an "undefined default export" error), suspend regardless of how this
      // particular rejection looks.
      if (isReloadInFlight() || (isModuleLoadError(err) && triggerStaleChunkReload())) {
        // Never-settling promise: keep React on the Suspense fallback (no
        // ErrorBoundary flash) until the page reloads out from under us.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }),
  );
}

/**
 * Global net for dynamic-import failures that don't flow through
 * `lazyWithReload` — preloaded CSS and any non-route `import()` (e.g. the lazily
 * loaded barcode scanner). Vite's preload helper dispatches `vite:preloadError`
 * on `window` for these. Call once during app bootstrap.
 */
export function installChunkReloadHandler(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', ((event: Event) => {
    // If we kick off a reload, swallow Vite's default rethrow so nothing crashes
    // in the brief moment before the navigation tears the page down.
    if (triggerStaleChunkReload()) event.preventDefault();
  }) as EventListener);
}
